/**
 * Bridge Event Stream — the web cloud bridge client.
 *
 * A real WebSocket client to the cloud bridge (`/ws/bridge`). It mirrors the
 * desktop daemon's bridge client (`shogunchop/crates/shogunchop/src/bridge.rs`):
 * open the socket, send a `bridge.identify` JSON-RPC request, then route
 * inbound JSON-RPC frames by method.
 *
 * This is the shared per-tab realtime transport for the web app (beyond chat):
 * session events, `platform_call` (`mcp.invoke`), indexing progress,
 * notification/error push, and cloud `workspace.filesChanged`. Desktop keeps
 * its daemon-owned cloud bridge and relays those frames over localhost
 * `/events` instead of opening this socket from the renderer.
 *
 * The Go search worker fans indexing progress out to every connected device of
 * an account via `AccountScope` (`go/internal/search/progress.go`) — cloud-origin
 * events have no originating device, so the UI must filter by the payload
 * `workspace_id`.
 */

import type { LlmProgressFrame, WebSocketFactory, WebSocketLike } from '../chat/types.js';
import { handlePlatformCallFrame, type McpInvokeFrame } from '../chat/bridge-executor.js';
import type { SessionRegistrar } from './bridge-session-source.js';
import type {
  NotificationCursor,
  NotificationEventFrame,
  ErrorEventFrame,
} from '../types/notification-types.js';
import type {
  SubscribeWorkspaceEventsRequest,
  WorkspaceProjection,
} from '../types/workspace-state.js';
import type { FeedPost } from '../types/feed.js';
import type { SupportEvent } from '../types/support.js';
import { workspaceProjectionFromBridgeFrame } from '../workspace-projection.js';
import {
  WORKSPACE_CONTEXT_OBSERVED,
  parseWorkspaceContextObserved,
} from '../workspace-context-observed.js';
import type { WorkspaceContextObserved } from '../types/context.js';
import {
  CatalogHolderRegistry,
  type CatalogChanged,
  type CatalogChangedHandler,
  type CatalogRegistrar,
} from './catalogs.js';

/**
 * Wraps the HTTP notification subscribe/unsubscribe control plane for the
 * shared bridge stream. The stream calls `subscribe` on every (re)connect with
 * its current keyset cursor so a reconnect drains the durable backlog missed
 * while offline; the SDK dedups the replay-vs-live overlap by `event_id`.
 */
export interface NotificationRegistrar {
  subscribe(deviceId: string, after?: NotificationCursor): Promise<unknown>;
  unsubscribe(deviceId: string): Promise<unknown>;
}

export interface FeedRegistrar {
  subscribe(deviceId: string): Promise<unknown>;
}

/**
 * Durable home for the push keyset cursor, supplied by the host app (browser:
 * `localStorage`). Without one the cursor lives only in the stream instance, so
 * a cold launch subscribes with no `after` and the backend replays the whole
 * account log from the beginning (events ADR-012).
 */
export interface NotificationCursorStore {
  load(): NotificationCursor | null;
  save(cursor: NotificationCursor): void;
}

/**
 * Per-frame delivery metadata. `replay` is TRUE for a frame drained from the
 * durable backlog on (re)connect (the plural `notification.events`/`error.events`
 * batches), FALSE for a live fanout frame (singular `notification.event`/
 * `error.event`). Consumers that must act ONLY on live events (e.g. the toast
 * dispatcher — replaying a whole history as toasts spams the user and overloads
 * the toast library) key off this; store/list feeders ignore it and consume both.
 */
export interface PushFrameMeta {
  replay: boolean;
}

/** Handler for a live/replayed notification frame. `meta.replay` distinguishes. */
export type NotificationEventHandler = (frame: NotificationEventFrame, meta: PushFrameMeta) => void;
/** Handler for a live/replayed error frame. `meta.replay` distinguishes. */
export type ErrorEventHandler = (frame: ErrorEventFrame, meta: PushFrameMeta) => void;
export type FeedEventHandler = (post: FeedPost, meta: PushFrameMeta) => void;
export type SupportEventHandler = (event: SupportEvent, meta: PushFrameMeta) => void;

/** Bounded cap on remembered push event_ids (replay-vs-live dedup). */
export const SEEN_PUSH_EVENT_CAP = 5000;

/**
 * Total-order comparator over the STRICT (created_at, id) keyset — MATCHES the
 * backend `(created_at, id)` tuple comparison so the client cursor never runs
 * ahead of an undelivered event (which would skip it). created_at is parsed at
 * full precision (seconds + fractional nanos), then id breaks ties
 * lexicographically (canonical-uuid order == Postgres uuid order). Returns >0 if
 * a is newer than b.
 */
export function comparePushKey(aTs: string, aId: string, bTs: string, bId: string): number {
  const [as, an] = parseTsKey(aTs);
  const [bs, bn] = parseTsKey(bTs);
  if (as !== bs) return as - bs;
  if (an !== bn) return an - bn;
  if (aId < bId) return -1;
  if (aId > bId) return 1;
  return 0;
}

/** Parse an RFC3339(Nano) UTC timestamp into [epochSeconds, fractionalNanos]. */
function parseTsKey(ts: string): [number, number] {
  const ms = Date.parse(ts);
  const secs = Number.isNaN(ms) ? 0 : Math.floor(ms / 1000);
  const m = /\.(\d+)/.exec(ts);
  const nanos = m ? parseInt(m[1].padEnd(9, '0').slice(0, 9), 10) : 0;
  return [secs, nanos];
}

/**
 * Handler invoked with one per-event params object (`{session_id, event_type,
 * data, sequence_num, created_at}`) for a subscribed session. Both the LIVE
 * `session.event` frame and each element of a REPLAY `session.events` (plural)
 * frame flow through the same handler, so live and replayed events are
 * indistinguishable downstream (dedup/sort by `sequence_num`).
 */
export type SessionEventHandler = (data: Record<string, unknown>) => void;
export type LlmProgressHandler = (frame: LlmProgressFrame) => void;

/**
 * Indexing progress for a single workspace, surfaced from the
 * `search.indexingProgress` bridge notification. CamelCase on the TS side;
 * mapped from the snake_case wire payload emitted by
 * `go/internal/search/progress.go`.
 */
export interface IndexingProgress {
  accountId: string;
  workspaceId: string;
  /** Chunks queued for embedding but not yet embedded. */
  queued: number;
  /** Chunks currently being embedded. */
  embedding: number;
  /** Chunks fully embedded and written to the search index. */
  indexed: number;
  /**
   * Composer readiness. `true` after a full/incremental emit has
   * finished embedding; `false` while indexing is in progress. Absent on
   * legacy frames is treated as not-ready by UI consumers.
   */
  ready: boolean;
  progress: 'idle' | 'indexing';
  health: 'ok' | 'degraded';
  emitterEpoch: number;
  indexRevision: number;
}

/** Handler invoked for each `search.indexingProgress` frame. */
export type IndexingProgressHandler = (progress: IndexingProgress) => void;

/**
 * workspace.filesChanged: a mutating file op ran in
 * the workspace's cloud pod. Payload is the workspace id only — clients
 * re-list their expanded directories themselves. Web consumers subscribe via
 * {@link BridgeEventStream.onWorkspaceFilesChanged} on the shared per-tab
 * stream; desktop rides the daemon's /events relay instead.
 */
export interface WorkspaceFilesChanged {
  workspaceId: string;
}
export type WorkspaceFilesChangedHandler = (change: WorkspaceFilesChanged) => void;

/**
 * flags.changed: a publish or kill touched
 * the named flag. Payload is the key only — clients re-evaluate via
 * `sdk.flags.evaluate` and never compute membership (flags ADR-002). The frame
 * rides the bridge ADR-003 BROADCAST lane — no client subscribe RPC, no
 * subscription row; the desktop daemon relays its own `flags.updated` event
 * over /events instead (see ShogunChopPushFrameStream.onFlagsUpdated).
 */
export interface FlagsChanged {
  key: string;
}
export type FlagsChangedHandler = (change: FlagsChanged) => void;

/**
 * workspace.contextObserved: an agent run just read this workspace's context
 * (`sdk.context.workspace.get`). Delivered to devices subscribed to the
 * workspace — the same audience as `workspace.stateChanged`, so hold
 * {@link BridgeEventStream.registerWorkspaceProjection} for the workspace.
 */
export type WorkspaceContextObservedHandler = (observed: WorkspaceContextObserved) => void;

export type WorkspaceProjectionHandler = (
  projection: WorkspaceProjection,
  source: 'snapshot' | 'event'
) => void;

export interface WorkspaceProjectionRegistrar {
  getState(workspaceId: string, deviceId?: string): Promise<WorkspaceProjection>;
  subscribeEvents(workspaceId: string, params: SubscribeWorkspaceEventsRequest): Promise<void>;
  unsubscribeEvents(workspaceId: string, deviceId: string): Promise<void>;
}

export class WorkspaceProjectionSynchronizer {
  private generation = 0;
  private connected = false;
  private ready = false;
  private revision = 0;
  private buffered: WorkspaceProjection[] = [];
  private connectionPromise: Promise<void> | null = null;

  constructor(
    private readonly snapshot: () => Promise<WorkspaceProjection>,
    private readonly subscribe: (afterRevision: number) => Promise<void>,
    private readonly deliver: WorkspaceProjectionHandler
  ) {}

  isConnected(): boolean {
    return this.connected;
  }

  connectionClosed(): void {
    this.generation += 1;
    this.connected = false;
    this.ready = false;
    this.buffered = [];
    this.connectionPromise = null;
  }

  currentConnection(): Promise<void> {
    return this.connectionPromise ?? Promise.resolve();
  }

  connectionOpened(): Promise<void> {
    if (this.connected && this.connectionPromise) return this.connectionPromise;
    const generation = ++this.generation;
    this.connected = true;
    this.ready = false;
    this.buffered = [];
    this.connectionPromise = (async () => {
      const snapshot = await this.snapshot();
      if (!this.connected || generation !== this.generation) return;
      this.revision = snapshot.revision;
      this.deliver(snapshot, 'snapshot');
      await this.subscribe(snapshot.revision);
      if (!this.connected || generation !== this.generation) return;
      const buffered = this.buffered.sort((left, right) => left.revision - right.revision);
      this.buffered = [];
      for (const projection of buffered) this.acceptReady(projection);
      this.ready = true;
    })();
    return this.connectionPromise;
  }

  accept(projection: WorkspaceProjection): void {
    if (!this.connected) return;
    if (!this.ready) {
      this.buffered.push(projection);
      return;
    }
    this.acceptReady(projection);
  }

  private acceptReady(projection: WorkspaceProjection): void {
    if (projection.revision <= this.revision) return;
    this.revision = projection.revision;
    this.deliver(projection, 'event');
  }
}

/**
 * Tenant identity the bridge requires to route account-scoped fanout to this
 * connection. All three are read from the caller's JWT claims + device config.
 */
export interface BridgeIdentity {
  deviceId: string;
  organizationId: string;
  accountId: string;
  /** Bearer JWT. Browser clients send it as a WebSocket subprotocol (bridge ADR-008). */
  token: string;
}

/** The wire shape of the `search.indexingProgress` notification params. */
interface IndexingProgressWire {
  account_id: string;
  workspace_id: string;
  queued: number;
  embedding: number;
  indexed: number;
  ready?: boolean;
  progress: 'idle' | 'indexing';
  health: 'ok' | 'degraded';
  emitter_epoch: number;
  index_revision: number;
}

/** Minimal JSON-RPC envelope we route on (notifications and requests). */
interface JsonRpcFrame {
  id?: string | number;
  method?: string;
  params?: unknown;
}

/**
 * Derive the bridge WebSocket URL from the API base URL. MIRRORS
 * `derive_bridge_url` (Rust) and `httpToWs` (VS Code) — keep in sync if the
 * derivation rules change. The bridge route is served at `/ws/bridge` on the
 * same host as the API (Kong fans both off the same gateway).
 */
export function deriveBridgeUrl(apiBaseUrl: string): string {
  const u = new URL(apiBaseUrl);
  const proto = u.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${u.host}/ws/bridge`;
}

export const BRIDGE_WS_PROTOCOL = 'localagent.bridge.v1';

export function bridgeAuthProtocols(token: string): string[] {
  return [BRIDGE_WS_PROTOCOL, token];
}

/**
 * A live subscription to the cloud bridge for a single identity. Construct it
 * via {@link BridgeEventStream}; register handlers with {@link onIndexingProgress};
 * tear it down with {@link close}.
 */
export class BridgeEventStream {
  private ws: WebSocketLike | null = null;
  private url: string;
  private readonly wsFactory: WebSocketFactory;
  private identity: BridgeIdentity;
  private readonly bridgeBaseUrl: string;
  private indexingHandlers = new Set<IndexingProgressHandler>();
  private filesChangedHandlers = new Set<WorkspaceFilesChangedHandler>();
  private flagsChangedHandlers = new Set<FlagsChangedHandler>();
  private contextObservedHandlers = new Set<WorkspaceContextObservedHandler>();
  private workspaceProjectionSynchronizers = new Map<
    string,
    Set<WorkspaceProjectionSynchronizer>
  >();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;

  // --- Identify-ack gate (fixes the onopen subscribe → verifyDeviceOwnership race) ---
  // The HTTP subscribe control-plane (session + notification subscribe) 403s unless
  // the caller's device already has an `active_connections` row — a row the bridge
  // server writes only when it PROCESSES the `bridge.identify` frame. Firing the
  // subscribes in `onopen` immediately after sending identify raced that write:
  // the parallel HTTP subscribe read reached go-api before the WS identify write
  // landed, so `verifyDeviceOwnership` found no row and returned 403 (silently
  // breaking durable replay; the console error was the visible symptom). The
  // server ALREADY acks identify with a JSON-RPC result — we just weren't waiting
  // for it. We now send identify as a REQUEST (with `id`) and open a fresh gate
  // per connect; the subscribes fire only after the ack resolves it.
  private identifyCounter = 0;
  private pendingIdentifyId: string | null = null;
  private identifiedPromise!: Promise<void>;
  private resolveIdentified!: () => void;
  private rejectIdentified!: (err: unknown) => void;
  /** True while identifiedPromise is unsettled (waiting for identify ack). */
  private identifyGatePending = false;

  // --- Session-event demux ---
  // A SET of handlers per session: two ChatSessions for the same session
  // (chat view + preview/picker, or a stale instance during navigation) must not
  // clobber each other. registerSession ADDS; the last one out unsubscribes.
  private sessionHandlers = new Map<string, Set<SessionEventHandler>>();
  private llmProgressHandlers = new Map<string, Set<LlmProgressHandler>>();
  // sessionId → SET of cursor providers; cursorFor() takes the max across the set
  // (keep max/last registered cursor). Keyed by sessionId so a reconnect can
  // re-issue subscribe with the current cursor for every open session.
  private sessionCursors = new Map<string, Set<() => number>>();
  // Wraps the HTTP subscribe/unsubscribe control-plane actions. Null for
  // the status-plane-only consumers (indexing progress / platform_call) that
  // never call registerSession.
  private readonly registrar: SessionRegistrar | null;

  // --- Notification/error push demux ---
  // App-wide (NOT per-session): errors + notifications stream on every view.
  private notificationHandlers = new Set<NotificationEventHandler>();
  private errorHandlers = new Set<ErrorEventHandler>();
  private feedHandlers = new Set<FeedEventHandler>();
  private supportHandlers = new Set<SupportEventHandler>();
  // Replay-vs-live dedup by event_id (= events.id) — NOT sequence_num.
  // An event around subscribe time (fanned live AND replayed) is delivered once.
  private seenPushEventIds = new Set<string>();
  // The max (created_at, id) seen — echoed back on reconnect so replay resumes
  // after it. Never runs ahead of an undelivered event (comparePushKey is exact).
  private notificationCursor: NotificationCursor | null = null;
  // Wraps the notification subscribe/unsubscribe HTTP control plane. Null for
  // consumers that never stream notifications (e.g. the test session-only stream).
  private readonly notificationRegistrar: NotificationRegistrar | null;
  private readonly workspaceProjectionRegistrar: WorkspaceProjectionRegistrar | null;
  private readonly cursorStore: NotificationCursorStore | null;
  private readonly catalogHolders: CatalogHolderRegistry | null;
  private readonly feedRegistrar: FeedRegistrar | null;
  private feedSubscriptionGate: Promise<void> | null = null;

  /**
   * Stream-level error callback. Reconnect-time re-register failures (`onopen`)
   * and last-one-out unsubscribe rejections route here (they do not pass through
   * a `registerSession` caller's `registered` promise). Set by the chat wiring.
   */
  onError?: (err: unknown) => void;

  /**
   * @param apiBaseUrl  API base URL (e.g. the Kong gateway URL). The bridge
   *                    URL is derived from it via {@link deriveBridgeUrl}.
   * @param identity    Tenant identity (deviceId/org/account/token) for the
   *                    `bridge.identify` handshake.
   * @param wsFactory   Platform WebSocket factory (browser: `(u) => new WebSocket(u)`).
   * @param registrar   Optional session registrar — required only to use
   *                    {@link registerSession} (the web session-event path).
   *                    Omitted by status-plane-only consumers.
   */
  constructor(
    apiBaseUrl: string,
    identity: BridgeIdentity,
    wsFactory: WebSocketFactory,
    registrar?: SessionRegistrar,
    notificationRegistrar?: NotificationRegistrar,
    workspaceProjectionRegistrar?: WorkspaceProjectionRegistrar,
    cursorStore?: NotificationCursorStore,
    catalogRegistrar?: CatalogRegistrar,
    feedRegistrar?: FeedRegistrar
  ) {
    this.identity = identity;
    this.feedRegistrar = feedRegistrar ?? null;
    this.bridgeBaseUrl = deriveBridgeUrl(apiBaseUrl);
    this.url = this.buildUrl();
    this.wsFactory = wsFactory;
    this.registrar = registrar ?? null;
    this.notificationRegistrar = notificationRegistrar ?? null;
    this.workspaceProjectionRegistrar = workspaceProjectionRegistrar ?? null;
    this.cursorStore = cursorStore ?? null;
    this.notificationCursor = this.cursorStore?.load() ?? null;
    this.catalogHolders = catalogRegistrar
      ? new CatalogHolderRegistry(
          () => this.identity.deviceId,
          catalogRegistrar,
          () => this.identifiedPromise,
          (err) => this.onError?.(err)
        )
      : null;
    this.connect();
  }

  private buildUrl(): string {
    return this.bridgeBaseUrl;
  }

  /**
   * Refresh the JWT used on the next (re)connect without closing the live
   * socket. Session renew must NOT recreate the stream — that drops
   * `bridge_subscriptions` and leaves already-connected ChatSessions deaf
   * (assistant messages never appear). The live socket stays identified; only
   * a later reconnect picks up the new token.
   */
  updateAuthToken(token: string): void {
    if (!token || token === this.identity.token) return;
    this.identity = { ...this.identity, token };
    this.url = this.buildUrl();
  }

  /** Fan one per-event params object to every registered handler for its session. */
  private dispatchSessionEvent(sessionId: string, params: Record<string, unknown>): void {
    const handlers = this.sessionHandlers.get(sessionId);
    if (!handlers) return; // frame for an unregistered session is dropped
    for (const handler of handlers) handler(params);
  }

  /**
   * Dedup a push frame by event_id, then advance the keyset cursor. Returns
   * false if the event_id was already seen (a replay-vs-live twin) so the caller
   * drops it. The bounded seen-set evicts oldest-first (insertion order).
   */
  private acceptPushEvent(eventId: string, createdAt: string): boolean {
    if (!eventId || this.seenPushEventIds.has(eventId)) return false;
    this.seenPushEventIds.add(eventId);
    if (this.seenPushEventIds.size > SEEN_PUSH_EVENT_CAP) {
      const oldest = this.seenPushEventIds.values().next().value;
      if (oldest !== undefined) this.seenPushEventIds.delete(oldest);
    }
    // Advance the cursor only when this event is strictly newer (never run ahead).
    if (
      createdAt &&
      (this.notificationCursor === null ||
        comparePushKey(
          createdAt,
          eventId,
          this.notificationCursor.created_at,
          this.notificationCursor.id
        ) > 0)
    ) {
      this.notificationCursor = { created_at: createdAt, id: eventId };
      this.cursorStore?.save(this.notificationCursor);
    }
    return true;
  }

  private dispatchNotificationFrame(frame: NotificationEventFrame, replay: boolean): void {
    if (!this.acceptPushEvent(frame.event_id, frame.created_at)) return;
    for (const handler of this.notificationHandlers) handler(frame, { replay });
  }

  private dispatchErrorFrame(frame: ErrorEventFrame, replay: boolean): void {
    if (!this.acceptPushEvent(frame.event_id, frame.created_at)) return;
    for (const handler of this.errorHandlers) handler(frame, { replay });
  }

  private dispatchFeedFrame(post: FeedPost, replay: boolean): void {
    for (const handler of this.feedHandlers) handler(post, { replay });
  }

  private dispatchSupportFrame(event: SupportEvent, replay: boolean): void {
    for (const handler of this.supportHandlers) handler(event, { replay });
  }

  /** Highest registered cursor for a session — the shared stream's cursor. */
  private cursorFor(sessionId: string): number {
    let max = 0;
    const cursors = this.sessionCursors.get(sessionId);
    if (cursors) for (const c of cursors) max = Math.max(max, c());
    return max;
  }

  /** Open a fresh unsettled identify gate (subscribe waiters park here). */
  private openIdentifyGate(): void {
    this.pendingIdentifyId = null;
    this.identifyGatePending = true;
    this.identifiedPromise = new Promise<void>((resolve, reject) => {
      this.resolveIdentified = () => {
        this.identifyGatePending = false;
        resolve();
      };
      this.rejectIdentified = (err: unknown) => {
        this.identifyGatePending = false;
        reject(err instanceof Error ? err : new Error(String(err)));
      };
    });
    // Hygiene (same pattern as registerSession): a reject before any waiter
    // attaches (e.g. natural disconnect mid-identify) must not become an
    // unhandledRejection / Playwright pageerror. Real awaiters still see it.
    this.identifiedPromise.catch(() => {});
  }

  private connect(): void {
    if (this.closed) return;
    // Reuse a pending gate opened by onclose during the reconnect delay so
    // registerSession waiters attached in that window are released by THIS
    // connection's identify ack (replacing the promise would orphan them).
    if (!this.identifyGatePending) {
      this.openIdentifyGate();
    } else {
      this.pendingIdentifyId = null;
    }
    // Pick up any token refreshed via updateAuthToken while we were offline.
    this.url = this.buildUrl();
    this.ws = this.wsFactory(this.url, bridgeAuthProtocols(this.identity.token));

    this.ws.onopen = () => {
      // Send identify as a JSON-RPC REQUEST (with `id`) so the server's
      // `{status:"identified"}` result can be correlated. Field names match the
      // Go BridgeIdentify struct (`deviceId`/`organizationId`/`accountId`).
      const identifyId = `identify-${++this.identifyCounter}`;
      this.pendingIdentifyId = identifyId;
      const identify = {
        jsonrpc: '2.0',
        id: identifyId,
        method: 'bridge.identify',
        params: {
          deviceId: this.identity.deviceId,
          organizationId: this.identity.organizationId,
          accountId: this.identity.accountId,
          protocolVersion: '1.0',
        },
      };
      this.ws?.send(JSON.stringify(identify));

      // Fire the HTTP subscribe control-plane ONLY AFTER the server acks identify
      // — by then it has written this device's `active_connections` row, so the
      // subscribe's `verifyDeviceOwnership` check finds the row instead of racing
      // it to a 403. An identify FAILURE rejects the gate and routes here loudly
      // (no silent connected-but-deaf stream; no-fallbacks).
      this.identifiedPromise
        .then(() => {
          // Re-register every open session for replay-gap-fill:
          // the disconnect cleanup dropped the subscription rows, so they must be
          // rewritten with the current cursor. A failure routes to the stream
          // error callback — note this re-register is NOT retried while the socket
          // stays healthy; the next actual WS drop re-runs onopen and retries it.
          // (Tighter same-socket recovery is `reregister`.)
          if (this.registrar) {
            for (const sessionId of this.sessionCursors.keys()) {
              this.registrar
                .subscribe(sessionId, this.identity.deviceId, this.cursorFor(sessionId))
                .catch((err) => this.onError?.(err));
            }
          }

          // (Re)subscribe the notification/error push stream with the current
          // keyset cursor so a reconnect drains the durable backlog missed while
          // offline. The SDK dedups the live/replay overlap by
          // event_id. A first connect has a null cursor → drain from the start.
          if (this.notificationRegistrar) {
            this.notificationRegistrar
              .subscribe(this.identity.deviceId, this.notificationCursor ?? undefined)
              .catch((err) => this.onError?.(err));
          }

          if (this.workspaceProjectionRegistrar) {
            for (const synchronizers of this.workspaceProjectionSynchronizers.values()) {
              for (const synchronizer of synchronizers) {
                synchronizer.connectionOpened().catch((err) => this.onError?.(err));
              }
            }
          }

          this.catalogHolders?.resubscribeAll();

          if (this.feedRegistrar && this.feedHandlers.size > 0) this.subscribeFeed();
        })
        .catch((err) => this.onError?.(err));
    };

    this.ws.onmessage = (msg) => {
      const frame = JSON.parse(msg.data) as JsonRpcFrame;
      // Identify ack: a JSON-RPC RESPONSE (has `id`, no `method`) to our
      // `bridge.identify` request. Releasing this gate is what unblocks the
      // onopen subscribes above — the server has now written the device's
      // `active_connections` row. An error result rejects the gate loudly.
      if (
        frame.method === undefined &&
        frame.id !== undefined &&
        frame.id === this.pendingIdentifyId
      ) {
        this.pendingIdentifyId = null;
        const err = (frame as { error?: unknown }).error;
        if (err) {
          this.rejectIdentified(new Error(`bridge identify failed: ${JSON.stringify(err)}`));
        } else {
          this.resolveIdentified();
        }
        return;
      }
      if (frame.method === 'search.indexingProgress') {
        const wire = frame.params as IndexingProgressWire;
        const progress: IndexingProgress = {
          accountId: wire.account_id,
          workspaceId: wire.workspace_id,
          queued: wire.queued,
          embedding: wire.embedding,
          indexed: wire.indexed,
          ready: wire.ready === true,
          progress: wire.progress,
          health: wire.health,
          emitterEpoch: wire.emitter_epoch,
          indexRevision: wire.index_revision,
        };
        for (const handler of this.indexingHandlers) handler(progress);
        return;
      }
      if (frame.method === 'mcp.invoke') {
        // Bidirectional bridge: the browser is a `platform_call` executor. Run
        // the registered executor (bound to the user's authenticated SDK) and
        // send the JSON-RPC response back over the SAME socket. Non-platform_call
        // tools yield `null` and are ignored. Never throws — the executor
        // surfaces failures as a typed `{status:"error"}` result.
        void handlePlatformCallFrame(frame as unknown as McpInvokeFrame).then((response) => {
          if (response !== null) {
            this.ws?.send(JSON.stringify(response));
          }
        });
        return;
      }
      if (frame.method === 'workspace.filesChanged') {
        const wire = frame.params as { workspace_id: string };
        for (const handler of this.filesChangedHandlers) {
          handler({ workspaceId: wire.workspace_id });
        }
        return;
      }
      if (frame.method === 'flags.changed') {
        const wire = frame.params as { key: string };
        for (const handler of this.flagsChangedHandlers) {
          handler({ key: wire.key });
        }
        return;
      }
      if (frame.method === WORKSPACE_CONTEXT_OBSERVED) {
        const observed = parseWorkspaceContextObserved(
          frame.params,
          `${WORKSPACE_CONTEXT_OBSERVED} params`
        );
        for (const handler of this.contextObservedHandlers) handler(observed);
        return;
      }
      const workspaceProjection = workspaceProjectionFromBridgeFrame(frame);
      if (workspaceProjection) {
        const synchronizers = this.workspaceProjectionSynchronizers.get(
          workspaceProjection.workspace_id
        );
        if (synchronizers) {
          for (const synchronizer of synchronizers) synchronizer.accept(workspaceProjection);
        }
        return;
      }
      if (frame.method === 'agent.llmProgress') {
        const p = frame.params as LlmProgressFrame;
        for (const handler of this.llmProgressHandlers.get(p.session_id) ?? []) handler(p);
        return;
      }
      if (frame.method === 'session.event') {
        // LIVE — one event (SessionScope fanout, PushSessionEvent). Demux by
        // session_id to the registered handler set.
        const p = frame.params as { session_id: string };
        this.dispatchSessionEvent(p.session_id, frame.params as Record<string, unknown>);
        return;
      }
      if (frame.method === 'session.events') {
        // REPLAY — one drained page (targeted Notify from ReplaySessionEvents).
        // Iterate the ordered array through the SAME per-event handler,
        // re-attaching session_id so each element is indistinguishable from a
        // live frame downstream (dedup/sort by sequence_num).
        const p = frame.params as {
          session_id: string;
          events: Record<string, unknown>[];
        };
        for (const e of p.events) {
          this.dispatchSessionEvent(p.session_id, { session_id: p.session_id, ...e });
        }
        return;
      }
      // --- Notification/error push demux ---
      // Singular = LIVE (AccountScope/UserScope fanout); plural = REPLAY (targeted
      // Notify from Notifications.ReplayEvents). Both flow through the same
      // dispatch, which dedups by event_id so an event fanned live AND
      // replayed around subscribe time is delivered once. A muted-category frame
      // is STILL dispatched — mute is display-time (events ADR-006).
      if (frame.method === 'notification.event') {
        // Singular = LIVE fanout.
        this.dispatchNotificationFrame(frame.params as NotificationEventFrame, false);
        return;
      }
      if (frame.method === 'notification.events') {
        // Plural = REPLAY of the durable backlog on (re)connect.
        const p = frame.params as { events: NotificationEventFrame[] };
        for (const e of p.events) this.dispatchNotificationFrame(e, true);
        return;
      }
      if (frame.method === 'error.event') {
        // Singular = LIVE fanout.
        this.dispatchErrorFrame(frame.params as ErrorEventFrame, false);
        return;
      }
      if (frame.method === 'error.events') {
        // Plural = REPLAY of the durable backlog on (re)connect.
        const p = frame.params as { events: ErrorEventFrame[] };
        for (const e of p.events) this.dispatchErrorFrame(e, true);
        return;
      }
      if (frame.method === 'catalog.changed') {
        this.catalogHolders?.dispatch(frame.params as CatalogChanged);
        return;
      }
      if (frame.method === 'feed.event') {
        this.dispatchFeedFrame(frame.params as FeedPost, false);
        return;
      }
      if (frame.method === 'feed.events') {
        const p = frame.params as { posts: FeedPost[] };
        for (const post of p.posts) this.dispatchFeedFrame(post, true);
        return;
      }
      if (frame.method === 'support.event') {
        this.dispatchSupportFrame(frame.params as SupportEvent, false);
        return;
      }
      // A JSON-RPC REQUEST (has an `id`) demands a response. If it reached a
      // browser device for a method this stream can't serve, we MUST NAK it —
      // silently dropping it hangs the caller for the full bridge timeout (the
      // backend LMStudioProvider waits 10 min) instead of failing loudly. The
      // canonical case: an lmstudio agent routes `llm.complete` to the session
      // device, but LM Studio is a DESKTOP-only local process — the browser can
      // never serve it, so a fast, clear error is the correct behavior (on
      // desktop the daemon's own bridge client handles `llm.complete`, not this
      // stream). Notifications (no `id`) stay ignored — session.event /
      // error.event etc. are handled by their own subscribers. Only inbound
      // REQUESTS (id AND method present) are NAK-able; a response frame (id +
      // result/error, no method) must not be treated as an unroutable request.
      if (frame.id !== undefined && frame.method !== undefined) {
        const message =
          frame.method === 'llm.complete'
            ? 'LM Studio is only available in the desktop app; this device cannot serve LM Studio completions'
            : `method not supported on this device: ${frame.method ?? '(unknown)'}`;
        this.ws?.send(
          JSON.stringify({ jsonrpc: '2.0', id: frame.id, error: { code: -32601, message } })
        );
      }
    };

    this.ws.onclose = () => {
      for (const synchronizers of this.workspaceProjectionSynchronizers.values()) {
        for (const synchronizer of synchronizers) synchronizer.connectionClosed();
      }
      this.ws = null;
      this.pendingIdentifyId = null;
      // Invalidate a RESOLVED identify gate so registerSession/reregister during
      // the reconnect window cannot subscribe against a deleted
      // active_connections row (verifyDeviceOwnership 403). If identify was
      // still pending, KEEP that gate — connect() reuses it and the next
      // identify ack releases the same waiters (no reject → no console/page
      // error on an expected reconnect).
      if (!this.identifyGatePending) {
        this.openIdentifyGate();
      }
      if (!this.closed) {
        this.reconnectTimer = setTimeout(() => this.connect(), 3000);
      }
    };
  }

  /** Subscribe to indexing-progress frames. Returns an unsubscribe function. */
  onIndexingProgress(handler: IndexingProgressHandler): () => void {
    this.indexingHandlers.add(handler);
    return () => {
      this.indexingHandlers.delete(handler);
    };
  }

  /** Subscribe to workspace.filesChanged frames. Returns an unsubscribe function. */
  onWorkspaceFilesChanged(handler: WorkspaceFilesChangedHandler): () => void {
    this.filesChangedHandlers.add(handler);
    return () => {
      this.filesChangedHandlers.delete(handler);
    };
  }

  /**
   * Subscribe to workspace.contextObserved frames for every workspace this
   * device is subscribed to. Returns an unsubscribe function.
   */
  onWorkspaceContextObserved(handler: WorkspaceContextObservedHandler): () => void {
    this.contextObservedHandlers.add(handler);
    return () => {
      this.contextObservedHandlers.delete(handler);
    };
  }

  /** Subscribe to flags.changed frames. Returns an unsubscribe function. */
  onFlagsChanged(handler: FlagsChangedHandler): () => void {
    this.flagsChangedHandlers.add(handler);
    return () => {
      this.flagsChangedHandlers.delete(handler);
    };
  }

  registerCatalog(
    scopeType: string,
    scopeId: string,
    handler: CatalogChangedHandler
  ): { registered: Promise<void>; unsubscribe: () => void } {
    if (!this.catalogHolders) {
      throw new Error(
        'BridgeEventStream.registerCatalog requires a CatalogRegistrar (construct the stream with one)'
      );
    }
    return this.catalogHolders.register(scopeType, scopeId, handler);
  }

  registerWorkspaceProjection(
    workspaceId: string,
    handler: WorkspaceProjectionHandler
  ): { registered: Promise<void>; unsubscribe: () => void } {
    if (!this.workspaceProjectionRegistrar) {
      throw new Error(
        'BridgeEventStream.registerWorkspaceProjection requires a workspace projection registrar'
      );
    }
    const registrar = this.workspaceProjectionRegistrar;
    const synchronizer = new WorkspaceProjectionSynchronizer(
      () => registrar.getState(workspaceId, this.identity.deviceId),
      (afterRevision) =>
        registrar.subscribeEvents(workspaceId, {
          device_id: this.identity.deviceId,
          after_revision: afterRevision,
        }),
      handler
    );
    const synchronizers =
      this.workspaceProjectionSynchronizers.get(workspaceId) ??
      new Set<WorkspaceProjectionSynchronizer>();
    synchronizers.add(synchronizer);
    this.workspaceProjectionSynchronizers.set(workspaceId, synchronizers);

    const registered = this.identifiedPromise.then(() => {
      if (!synchronizer.isConnected()) return synchronizer.connectionOpened();
    });
    registered.catch(() => {});

    const unsubscribe = (): void => {
      synchronizer.connectionClosed();
      synchronizers.delete(synchronizer);
      if (synchronizers.size === 0) {
        this.workspaceProjectionSynchronizers.delete(workspaceId);
        registrar
          .unsubscribeEvents(workspaceId, this.identity.deviceId)
          .catch((err) => this.onError?.(err));
      }
    };

    return { registered, unsubscribe };
  }

  /** Subscribe to notification push frames (live + replay). Returns an unsubscribe fn. */
  onNotificationEvent(handler: NotificationEventHandler): () => void {
    this.notificationHandlers.add(handler);
    return () => {
      this.notificationHandlers.delete(handler);
    };
  }

  /** Subscribe to error push frames (live + replay). Returns an unsubscribe fn. */
  onErrorEvent(handler: ErrorEventHandler): () => void {
    this.errorHandlers.add(handler);
    return () => {
      this.errorHandlers.delete(handler);
    };
  }

  onSupportEvent(handler: SupportEventHandler): () => void {
    this.supportHandlers.add(handler);
    return () => {
      this.supportHandlers.delete(handler);
    };
  }

  // The feed subscribe waits for the identify ack and re-runs per reconnect (sdk ADR-024).
  registerFeed(handler: FeedEventHandler): () => void {
    if (!this.feedRegistrar) {
      throw new Error(
        'BridgeEventStream.registerFeed requires a FeedRegistrar (construct the stream with one)'
      );
    }
    this.feedHandlers.add(handler);
    if (this.feedHandlers.size === 1) this.feedSubscriptionGate = null;
    this.subscribeFeed();
    return () => {
      this.feedHandlers.delete(handler);
    };
  }

  private subscribeFeed(): void {
    const gate = this.identifiedPromise;
    if (this.feedSubscriptionGate === gate) return;
    this.feedSubscriptionGate = gate;
    const registrar = this.feedRegistrar!;
    gate
      .then(() => registrar.subscribe(this.identity.deviceId))
      .catch((err: unknown) => {
        if (this.feedSubscriptionGate === gate) this.feedSubscriptionGate = null;
        this.onError?.(err);
      });
  }

  /**
   * Register interest in a session's events over this shared connection.
   * Adds `handler` to the session's demux set, records the `cursor` provider, and opens the HTTP subscription via the
   * registrar.
   *
   * @returns `registered` — the initial subscribe promise, PROPAGATED (never
   *          `void`): the caller MUST await/surface it so a 403/404/network
   *          failure is observable, not a silent connected-but-deaf chat
   *          (no-fallbacks). `unsubscribe` removes ONLY this handler; when the
   *          session's set empties, it deletes the maps' entries AND deregisters
   *          the subscription (last-one-out), routing any rejection to
   *          {@link onError}. It resolves once that deregistration settles.
   */
  /**
   * Receive `agent.llmProgress` frames for a session (agents ADR-bf03c). They
   * ride the session's existing subscription, so this opens nothing; it only
   * demuxes. Returns the unsubscribe.
   */
  onLlmProgress(sessionId: string, handler: LlmProgressHandler): () => void {
    const handlers = this.llmProgressHandlers.get(sessionId) ?? new Set<LlmProgressHandler>();
    handlers.add(handler);
    this.llmProgressHandlers.set(sessionId, handlers);
    return () => {
      handlers.delete(handler);
      if (handlers.size === 0) this.llmProgressHandlers.delete(sessionId);
    };
  }

  registerSession(
    sessionId: string,
    handler: SessionEventHandler,
    cursor: () => number
  ): { registered: Promise<void>; unsubscribe: () => Promise<void> } {
    if (!this.registrar) {
      // Fail loud — a stream built without a registrar cannot open a
      // subscription; there is no silent degradation (no-fallbacks).
      throw new Error(
        'BridgeEventStream.registerSession requires a SessionRegistrar (construct the stream with one)'
      );
    }
    const registrar = this.registrar;

    const handlers = this.sessionHandlers.get(sessionId) ?? new Set<SessionEventHandler>();
    const cursors = this.sessionCursors.get(sessionId) ?? new Set<() => number>();
    handlers.add(handler);
    cursors.add(cursor);
    this.sessionHandlers.set(sessionId, handlers);
    this.sessionCursors.set(sessionId, cursors);

    // Propagate — no `void`. The caller decides how to surface a rejection.
    // Gate on the identify ack (same race as the onopen subscribes): if the
    // socket is already identified the promise is resolved and this is a
    // microtask hop; if it is mid-(re)connect the subscribe waits for the
    // device's `active_connections` row to exist before hitting the endpoint.
    const registered = this.identifiedPromise.then(() =>
      registrar.subscribe(sessionId, this.identity.deviceId, this.cursorFor(sessionId))
    );

    // Async-hygiene leak guard (NOT error-hiding). `registered` is DEFERRED
    // (`identifiedPromise.then(subscribe)`), so its rejection settles a microtask
    // (or a whole reconnect) LATER than this synchronous return. `registered` is
    // still the caller-facing surface — anyone who `await`s / `.catch`es it sees
    // the 403/404/network rejection (their handler fires independently of the
    // no-op below). But a caller that DISCARDS it, or whose consumption loses a
    // race with a background reconnect that invalidates the subscription
    // (deleted session -> 404, torn-down/cross-tenant device -> 403), would
    // otherwise leak an *unhandled* rejection — which fails the whole test runner
    // (exit 1 despite green tests) and can crash a strict web runtime. Attaching
    // a no-op catch to a SEPARATE branch marks the promise handled for the
    // un-awaited path WITHOUT consuming the rejection for real awaiters. The
    // authoritative reconnect re-subscribe already routes to `onError`; this only
    // covers the deferred initial-subscribe.
    registered.catch(() => {});

    const unsubscribe = (): Promise<void> => {
      handlers.delete(handler);
      cursors.delete(cursor);
      if (handlers.size > 0) return Promise.resolve();
      this.sessionHandlers.delete(sessionId);
      this.sessionCursors.delete(sessionId);
      const deregistered = registrar.unsubscribe(sessionId, this.identity.deviceId);
      deregistered.catch((err) => this.onError?.(err));
      return deregistered;
    };

    return { registered, unsubscribe };
  }

  /**
   * Imperative SAME-SOCKET re-subscribe for one session (gap-fill —
   * the "tighter same-socket recovery" the `onopen` re-register comment
   * names). Re-issues `subscribe` with the
   * session's CURRENT cursor (`cursorFor` = the max of the registered cursor
   * thunks, which the chat layer now feeds `contiguousMax`) over the LIVE socket
   * — no WS drop required. The resulting replay drains everything after the
   * cursor, filling a detected sub-max gap.
   *
   * Rejects loudly (no-fallbacks): no registrar, or an unregistered session, is a
   * programming error; a subscribe non-2xx propagates to the caller.
   */
  reregister(sessionId: string): Promise<void> {
    if (!this.registrar) {
      throw new Error(
        'BridgeEventStream.reregister requires a SessionRegistrar (construct the stream with one)'
      );
    }
    if (!this.sessionCursors.has(sessionId)) {
      throw new Error(`BridgeEventStream.reregister: session ${sessionId} is not registered`);
    }
    // Same identify-ack gate as registerSession / onopen — a gap-fill re-subscribe
    // during a reconnect window must not race verifyDeviceOwnership.
    return this.identifiedPromise.then(() =>
      this.registrar!.subscribe(sessionId, this.identity.deviceId, this.cursorFor(sessionId))
    );
  }

  /** Close the socket and stop reconnecting. */
  close(): void {
    this.catalogHolders?.close();
    this.closed = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      // Prevent onclose from opening a reconnect gate for a stream the app is
      // tearing down (React effect remount / token rotate). Do NOT reject the
      // identify gate here: intentional teardown races Strict-Mode remount and
      // a reject with no waiter becomes a Playwright pageerror that fails e2e
      // console gates while the replacement stream is already being opened.
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }
}
